import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionStatus = ({ user, userProfile, isPremium, onCancelSubscription, onReactivateSubscription }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Use the isPremium prop passed from parent (calculated in AuthContext)
  // Fallback to local calculation if not provided
  const isUserPremium = isPremium !== undefined ? isPremium : 
                       (userProfile?.role === 'premium') || 
                       user?.email === 'info@skillbinder.com' || 
                       user?.email === 'glenn@epoxydogs.com';

  // Real subscription data - will be loaded from database
  const subscription = null; // TODO: Load from database based on user subscription

  const handleCancelSubscription = async () => {
    setIsProcessing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsProcessing(false);
    setShowCancelModal(false);
    onCancelSubscription?.();
  };

  const handleReactivateSubscription = async () => {
    setIsProcessing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsProcessing(false);
    onReactivateSubscription?.();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: 'text-success',
        bg: 'bg-success/10',
        icon: 'CheckCircle',
        text: 'Active'
      },
      canceled: {
        color: 'text-warning',
        bg: 'bg-warning/10',
        icon: 'AlertCircle',
        text: 'Canceled'
      },
      past_due: {
        color: 'text-error',
        bg: 'bg-error/10',
        icon: 'XCircle',
        text: 'Past Due'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.active;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${config?.bg} ${config?.color}`}>
        <Icon name={config?.icon} size={14} />
        <span>{config?.text}</span>
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilRenewal = () => {
    if (!subscription?.nextBillingDate) return 0;
    const today = new Date();
    const renewalDate = new Date(subscription.nextBillingDate);
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!isUserPremium) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <Icon name="User" size={24} className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Free Account</h3>
              <p className="text-muted-foreground">Basic features included</p>
            </div>
          </div>
          <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium bg-secondary/10 text-secondary">
            <Icon name="User" size={14} />
            <span>Free</span>
          </span>
        </div>

        <div className="space-y-4 mb-6">
          {/* Included Features Section */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Included</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={14} className="text-success" />
                <span className="text-muted-foreground">1 Career Assessment</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={14} className="text-success" />
                <span className="text-muted-foreground">1 Saved Plan</span>
              </div>
              <div className="flex items-center space-x-2 text-sm col-span-2">
                <Icon name="Check" size={14} className="text-success" />
                <span className="text-muted-foreground">2 Exercises per Week</span>
              </div>
              <div className="flex items-center space-x-2 text-sm col-span-2">
                <Icon name="Check" size={14} className="text-success" />
                <span className="text-muted-foreground">AI Plan (1/mo, short-term 2–4 weeks, basic)</span>
              </div>
            </div>
          </div>
          {/* Excluded Features Section */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Not Included</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">3 Career Assessments per month (included)</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Unlimited Saved Plans</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Unlimited Exercises</span>
              </div>
              {/* AI Plan is available to free tier; omit from exclusions */}
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Resume Builder</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Salary Insights by ZIP</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Personalized Development Plans</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Export Capabilities</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">All 900+ Occupations</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Icon name="X" size={14} className="text-red-500" />
                <span className="text-muted-foreground">Priority Email Support</span>
              </div>
            </div>
          </div>
        </div>

        <Button variant="default" fullWidth iconName="Crown" iconPosition="left">
          Upgrade to Premium
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Crown" size={24} className="text-warning" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Premium Subscription</h3>
              <p className="text-muted-foreground">{subscription?.plan}</p>
            </div>
          </div>
          {getStatusBadge(subscription?.status)}
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Subscription Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-foreground mb-3">Billing Information</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium text-foreground">${subscription?.amount}/month</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Next billing date</span>
                <span className="font-medium text-foreground">{subscription?.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Days until renewal</span>
                <span className="font-medium text-foreground">{getDaysUntilRenewal()} days</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-foreground mb-3">Current Period</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Started</span>
                <span className="font-medium text-foreground">{formatDate(subscription?.currentPeriodStart)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Ends</span>
                <span className="font-medium text-foreground">{formatDate(subscription?.currentPeriodEnd)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Auto-renewal</span>
                <span className="font-medium text-foreground">
                  {subscription?.cancelAtPeriodEnd ? 'Disabled' : 'Enabled'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Premium Features */}
        <div>
          <h4 className="font-medium text-foreground mb-3">Premium Features</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              '3 Career Assessments per month (included)',
              'AI-powered plan generation',
              'Full plan details & insights',
              'All exercises & activities',
              'Weekly progress reminders',
              'CSV & PDF export',
              'Custom KPI tracking',
              'Priority support'
            ]?.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={16} className="text-success" />
                <span className="text-muted-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          {subscription?.status === 'active' && !subscription?.cancelAtPeriodEnd && (
            <>
              <Button variant="outline" iconName="Edit" iconPosition="left">
                Change Plan
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => setShowCancelModal(true)}
                iconName="X" 
                iconPosition="left"
              >
                Cancel Subscription
              </Button>
            </>
          )}

          {subscription?.cancelAtPeriodEnd && (
            <Button 
              variant="default" 
              loading={isProcessing}
              onClick={handleReactivateSubscription}
              iconName="RotateCcw" 
              iconPosition="left"
            >
              Reactivate Subscription
            </Button>
          )}

          {subscription?.status === 'past_due' && (
            <Button variant="default" iconName="CreditCard" iconPosition="left">
              Update Payment Method
            </Button>
          )}
        </div>

        {/* Cancellation Notice */}
        {subscription?.cancelAtPeriodEnd && (
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div>
                <h4 className="text-medium text-warning mb-1">Subscription Canceled</h4>
                <p className="text-sm text-warning/80">
                  Your subscription will end on {formatDate(subscription?.currentPeriodEnd)}. 
                  You'll continue to have Premium access until then.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setShowCancelModal(false)}
          />
          <div className="relative bg-card rounded-lg shadow-lg border border-border max-w-md w-full p-6 space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertTriangle" size={32} className="text-warning" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Cancel Subscription?</h3>
              <p className="text-muted-foreground">
                You'll lose access to Premium features at the end of your current billing period 
                ({formatDate(subscription?.currentPeriodEnd)}).
              </p>
            </div>

            <div className="space-y-2 text-sm">
              <p className="font-medium text-foreground">You'll lose access to:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• 3 Career Assessments per month (included)</li>
                <li>• AI-powered plan generation</li>
                <li>• Export capabilities</li>
                <li>• Priority support</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowCancelModal(false)}
              >
                Keep Subscription
              </Button>
              <Button
                variant="destructive"
                fullWidth
                loading={isProcessing}
                onClick={handleCancelSubscription}
              >
                Cancel Subscription
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;